import type { ComponentType } from 'react'

import { IMPLICIT_MODULE } from './connectors'

/**
 * Договор формы подключения native-пакета (RP-UA1a). Общий экран реестра не
 * знает ни одного вида ресурса: форму настроек, подпись секрета и проверку
 * черновика даёт пакет вида (`resource-onec-ui`, `resource-aqsi-ui`), а
 * приложение регистрирует её в composition. Ветвления по виду в Platform нет.
 *
 * Какие права и модули у вида, форма НЕ объявляет: это данные манифеста вида (ADR-22), клиент читает их с сервера
 * (`platformConnectors`). Пакет вида даёт только слова к ним: подписи и пояснения прав и модулей.
 *
 * Native-пакет не импортирует `platform-ui` (матрица зависимостей): он
 * объявляет объект той же формы, а совместимость проверяет компилятор при
 * регистрации в `apps/web` (`readonly ConnectionFormExtension[]`).
 */

/** Замечание к полю настроек: путь поля и текст по-русски. */
export interface ConnectionSettingsIssue {
  field: string
  message: string
}

/** Ресурс реестра, из которого форма вида выбирает связанный (например, базу 1С для источника без своей службы). */
export interface ConnectionFormResource {
  id: string
  kind: string
  displayName: string
  enabled: boolean
}

/** Вариант выбора, который форма вида запросила у своего хука `useChoices`; `group` — к чему относится (например, rid базы). */
export interface ConnectionFormChoice {
  value: string
  label: string
  group?: string
}

/** Что редактор ресурса даёт форме вида сверх черновика: он сам запросов не делает, форма — тоже. */
export interface ConnectionFormContext {
  /** Идентификатор правимого ресурса; `null` — ресурс создаётся. */
  resourceId: string | null
  /** Ресурсы реестра (читает редактор): форма выбирает из них связанные. */
  resources: readonly ConnectionFormResource[]
  /** Варианты, которые вернул `useChoices` вида, по имени поля. */
  choices: Readonly<Record<string, readonly ConnectionFormChoice[]>>
}

export const EMPTY_CONNECTION_FORM_CONTEXT: ConnectionFormContext = { resourceId: null, resources: [], choices: {} }

export interface ConnectionFormProps {
  /** Черновик native-настроек — JSON по схеме вида. */
  settings: Readonly<Record<string, unknown>>
  onChange: (settings: Record<string, unknown>) => void
  disabled: boolean
  issues: readonly ConnectionSettingsIssue[]
  /** Реестр и варианты выбора; формы, которым они не нужны, поле игнорируют. */
  context?: ConnectionFormContext
}

export interface ConnectionSecretSpec {
  /** Подпись поля: «Пароль пользователя 1С», «Ключ кабинета AQSI». */
  label: string
  /** Подсказка под полем при правке: секрет не показывается и не возвращается. */
  hint: string
  /** Без секрета новый ресурс не подключится. */
  requiredOnCreate: boolean
}

export interface ConnectionFormExtension {
  /** Вид ресурса в реестре Platform: `onec`, `aqsi`. */
  kind: string
  /** Подпись вида для человека. */
  title: string
  /**
   * Короткая метка вида — значок на вкладке раздела («1С», «AQSI», UI-SECTIONS).
   * Нет — используется `title`.
   */
  shortTitle?: string
  /** Слово вида на плитке источника («Кабинет AQSI», «База 1С», UI-RMK-FLOW 3.4). Нет — используется `title`. */
  sourceNoun?: string
  /**
   * Цвет вида — имя палитры источника ui-kit (`--krp-source-<tone>`, атрибут `data-source`), тот же, что у рамки окон пакета:
   * плитки, вкладки и списки источников окрашены по виду (решение владельца 03.11.2026). Нет — источник без своего цвета.
   */
  tone?: string
  /** Одна короткая строка на плитке в окне «Добавить источник»: что это за источник. Нет — плитка без строки. */
  summary?: string
  /** Подписи прав вида словами для человека: «catalog.read» → «Чтение каталога». Нет подписи — показывается код. */
  capabilityLabels?: Readonly<Record<string, string>>
  /** Что право открывает человеку — одной фразой (п. 10 замечаний 13.10.2026). Нет пояснения — показывается только название. */
  capabilityHints?: Readonly<Record<string, string>>
  /** Названия модулей вида словами по коду модуля из манифеста: «catalog» → «Каталог и цены». Нет названия — см. `moduleTitleOf`. */
  moduleTitles?: Readonly<Record<string, string>>
  /** Что даёт модуль — одной фразой на карточке источника. Нет пояснения — только название. */
  moduleHints?: Readonly<Record<string, string>>
  secret: ConnectionSecretSpec | null
  /** Пустые настройки нового ресурса — уже по схеме вида. */
  emptySettings: () => Record<string, unknown>
  /** Проверка черновика до отправки. Окончательно решает сервер по JSON-схеме вида. */
  validate: (settings: Readonly<Record<string, unknown>>) => readonly ConnectionSettingsIssue[]
  /** Экран настроек: только значение и колбэк, без store и запросов. */
  Form: ComponentType<ConnectionFormProps>
  /**
   * Хук вида: варианты выбора для формы (по имени поля), которые нужно прочитать с сервера. Вызывается компонентом
   * редактора при каждой отрисовке в одном и том же порядке; форма получает результат в `context.choices`.
   */
  useChoices?: (resourceId: string | null) => Readonly<Record<string, readonly ConnectionFormChoice[]>>
}

export interface ConnectionFormRegistry {
  find: (kind: string) => ConnectionFormExtension | undefined
  all: readonly ConnectionFormExtension[]
  /** Подпись вида: из формы пакета, иначе сам код вида. */
  titleOf: (kind: string) => string
  /** Короткая метка вида (вкладки разделов): `shortTitle`, иначе `title`, иначе код вида. */
  shortTitleOf: (kind: string) => string
  /** Слово вида на плитке источника: `sourceNoun`, иначе `title`, иначе код вида. */
  sourceNounOf: (kind: string) => string
  /** Строка о виде на плитке добавления; `null` — форма её не дала. */
  summaryOf: (kind: string) => string | null
  /** Цвет вида (`data-source`); `null` — у формы его нет. */
  toneOf: (kind: string) => string | null
  /**
   * Порядок источников во всех списках (решение владельца 03.11.2026): виды — в порядке регистрации форм, вид без формы — после
   * известных, внутри вида — по названию. Общий каталог, где он есть, стоит перед всеми источниками — его ставит сам список.
   */
  compareSources: (left: SourceOrderKey, right: SourceOrderKey) => number
  /** Порядок самих видов (плитки «Добавить источник», вкладки «недоступно») — тот же, что у источников. */
  compareKinds: (left: string, right: string) => number
  /** Копия списка в порядке `compareSources`; сам список не меняется. */
  sortSources: <T extends SourceOrderKey>(sources: readonly T[]) => T[]
  /** Подпись права вида: из формы пакета, иначе сам код права. */
  capabilityLabelOf: (kind: string, capability: string) => string
  /** Пояснение права вида; `null` — у формы его нет. */
  capabilityHintOf: (kind: string, capability: string) => string | null
  /**
   * Название модуля вида: из формы пакета; неявный модуль `main` (вид без раздела модулей) называется как сам вид; иначе
   * код модуля.
   */
  moduleTitleOf: (kind: string, moduleId: string) => string
  /** Пояснение модуля; `null` — у формы его нет. */
  moduleHintOf: (kind: string, moduleId: string) => string | null
}

/** Чем источник упорядочивается в списках: вид и название; при равных названиях — id, чтобы порядок не зависел от ответа сервера. */
export interface SourceOrderKey {
  kind: string
  displayName: string
  id?: string
}

/** Реестр форм: каждый вид — одна форма; повтор — ошибка сборки приложения. */
export function createConnectionFormRegistry(forms: readonly ConnectionFormExtension[]): ConnectionFormRegistry {
  const byKind = new Map<string, ConnectionFormExtension>()
  for (const form of forms) {
    if (form.kind.trim() === '') throw new Error('Форма подключения без вида ресурса')
    if (byKind.has(form.kind)) throw new Error(`Форма подключения вида «${form.kind}» зарегистрирована дважды`)
    byKind.set(form.kind, form)
  }
  const all = [...byKind.values()]
  const rank = new Map(all.map((form, index) => [form.kind, index]))
  const rankOf = (kind: string): number => rank.get(kind) ?? all.length
  const compareKinds = (left: string, right: string): number => rankOf(left) - rankOf(right) || left.localeCompare(right)
  const compareSources = (left: SourceOrderKey, right: SourceOrderKey): number =>
    compareKinds(left.kind, right.kind) ||
    left.displayName.localeCompare(right.displayName, 'ru') ||
    (left.id ?? '').localeCompare(right.id ?? '')
  return {
    find: (kind) => byKind.get(kind),
    all,
    titleOf: (kind) => byKind.get(kind)?.title ?? kind,
    shortTitleOf: (kind) => byKind.get(kind)?.shortTitle ?? byKind.get(kind)?.title ?? kind,
    sourceNounOf: (kind) => byKind.get(kind)?.sourceNoun ?? byKind.get(kind)?.title ?? kind,
    summaryOf: (kind) => byKind.get(kind)?.summary ?? null,
    toneOf: (kind) => byKind.get(kind)?.tone ?? null,
    compareSources,
    compareKinds,
    sortSources: (sources) => [...sources].sort(compareSources),
    capabilityLabelOf: (kind, capability) => byKind.get(kind)?.capabilityLabels?.[capability] ?? capability,
    capabilityHintOf: (kind, capability) => byKind.get(kind)?.capabilityHints?.[capability] ?? null,
    moduleTitleOf: (kind, moduleId) =>
      byKind.get(kind)?.moduleTitles?.[moduleId] ?? (moduleId === IMPLICIT_MODULE ? (byKind.get(kind)?.title ?? kind) : moduleId),
    moduleHintOf: (kind, moduleId) => byKind.get(kind)?.moduleHints?.[moduleId] ?? null,
  }
}

export const EMPTY_CONNECTION_FORMS: ConnectionFormRegistry = createConnectionFormRegistry([])
